import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useAuth } from './AuthProvider.tsx';
import type { TotpOppsett } from './typer.ts';
import { Ikon } from '../status/Ikon.tsx';

/* ------------------------------------------------------------------------------
   OPPSETTET AV AUTENTISERINGSAPPEN — ÉN KILDE, TO STEDER.

   Skjermen finnes på to steder som ikke ser like ut:

     `sider/Tofaktor.tsx`      foran låsen, som et kort midt på en tom flate. Kontoen har
                              ingen verifisert metode, og oppsettet ER hele skjermen.
     `sider/MinInnlogging.tsx` bak låsen, inne i en rad. Kontoen har allerede en metode og
                              legger til en til; rundt oppsettet står de andre radene.

   Det som er DET SAMME er ordene og oppførselen: QR-en som helt, nøkkelen for den som ikke
   kan skanne, `otpauth://`-lenken på en berøringsenhet, det sekssifrede feltet, og at
   hemmeligheten lages ÉN gang per montering. Derfor bor de her, i delte biter, og ikke i to
   filer som må holdes i takt for hånd. Formen rundt — kort eller rad, overskrift eller ikke,
   hvilke tekstknapper som står under — eier hver skjerm selv.

   NØKKELEN KOMMER ALDRI I UTKLIPPSTAVLEN (tofaktor-konsept v0.1, P3 a): den er den VARIGE
   hemmeligheten, og en utklippstavle med historikk og synkronisering beholder den. Ingen
   «Kopier» finnes, og utklippstavle-API-et nevnes ikke i denne filen i det hele tatt —
   vakt 12 i test/lint-vakter.test.ts leser BÅDE denne filen og `Tofaktor.tsx` som tekst og
   blir rød på navnet. Grupperingen i fire er CSS: DOM-teksten er den ugrupperte strengen.
------------------------------------------------------------------------------ */

/** QR eller nøkkel — to visninger av det SAMME oppsettet, aldri to oppsett. */
export type Visning = 'qr' | 'noekkel';

/** Overskriften oppsettet har akkurat nå (ordlisten). Skjermen velger hvor den står. */
export function appoppsettTittel(visning: Visning): string {
  return visning === 'noekkel' ? 'Skriv inn nøkkelen i appen' : 'Skann koden';
}

/**
 * `pointer: coarse` — samme mønster som breakpointen: spørringen måles, aldri gjettes.
 *
 * Der pekeren er grov er autentiseringsappen på SAMME enhet som QR-en, og koden kan ikke
 * skannes. Alternativet er da `otpauth://`-lenken i stedet for nøkkelen.
 */
export function useBeroering(): boolean {
  const [grov, setGrov] = useState(false);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return undefined;
    const mq = matchMedia('(pointer: coarse)');
    const oppdater = () => setGrov(mq.matches);
    oppdater();
    mq.addEventListener('change', oppdater);
    return () => mq.removeEventListener('change', oppdater);
  }, []);
  return grov;
}

/**
 * ÉN `TotpOppsett`, STARTET ÉN GANG PER MONTERING. `startOppsett` avregistrerer en
 * uverifisert faktor og lager en NY hemmelighet, så to kall er to hemmeligheter — og en
 * hemmelighet ingen ba om er en hemmelighet for mye. Løftet holdes i en ref, og StrictMode
 * (som kjører effekten to ganger) gjenbruker det i stedet for å kalle porten igjen.
 *
 * `aktiv` er skjermens svar på «skal oppsettet i det hele tatt lages nå?»: Tofaktor sier ja
 * først når appsporet velges, og «Min innlogging» først når raden åpnes.
 */
export function useAppoppsett(aktiv: boolean): {
  oppsett: TotpOppsett | null;
  startfeil: string | null;
  visning: Visning;
  setVisning: (v: Visning) => void;
  /** Starter på nytt etter en feilet start — ny hemmelighet, nytt forsøk. */
  paaNytt: () => void;
} {
  const auth = useAuth();
  const [oppsett, setOppsett] = useState<TotpOppsett | null>(null);
  const [startfeil, setStartfeil] = useState<string | null>(null);
  const [visning, setVisning] = useState<Visning>('qr');
  const løftet = useRef<Promise<TotpOppsett> | null>(null);

  const start = useCallback(
    (paaNytt: boolean) => {
      if (paaNytt) løftet.current = null;
      setStartfeil(null);
      const løfte = (løftet.current ??= auth.startOppsett());
      let levende = true;
      løfte
        .then((o) => {
          if (levende) setOppsett(o);
        })
        .catch((e: unknown) => {
          if (levende) setStartfeil(e instanceof Error ? e.message : 'Oppsettet kunne ikke starte.');
        });
      return () => {
        levende = false;
      };
    },
    [auth],
  );

  useEffect(() => {
    if (!aktiv) return undefined;
    return start(false);
  }, [aktiv, start]);

  return { oppsett, startfeil, visning, setVisning, paaNytt: () => start(true) };
}

/**
 * Helten: QR-koden, eller nøkkelen, eller plassholderen mens oppsettet lages. Plassholderen
 * har QR-ens mål så flaten ikke hopper når koden kommer.
 *
 * QR-flaten er HVIT uansett palett (tokenet `screen.qrBackdrop`): en mørk QR-kode er
 * uskannbar.
 */
export function Oppsettshelt({ oppsett, visning }: { oppsett: TotpOppsett | null; visning: Visning }) {
  if (visning === 'noekkel' && oppsett) {
    return (
      <code className="panel-noekkel-grupper">
        {(oppsett.noekkel.match(/.{1,4}/g) ?? []).map((gruppe, i) => (
          <span key={`${gruppe}-${i}`}>{gruppe}</span>
        ))}
      </code>
    );
  }
  if (oppsett) {
    return (
      <div className="panel-qr">
        <img src={oppsett.qr} alt="QR-kode til autentiseringsappen" />
      </div>
    );
  }
  return (
    <>
      <div className="panel-qr panel-qr-plassholder" aria-hidden="true" />
      <p role="status" className="panel-tekst">
        Lager oppsettet …
      </p>
    </>
  );
}

/**
 * Det sekssifrede feltet. Etiketten «Kode fra appen» finnes for skjermleseren ALLTID; er
 * overskriften over noe annet («Skann koden»), bærer en dekorativ linje den for øyet også —
 * to kilder til samme navn ville gitt feltet to etiketter.
 *
 * Feilen står VED feltet med ikon og rød ramme (`aria-invalid`), aldri som en melding under
 * knappene, og aldri som farge alene.
 */
export function Kodefelt({
  id = 'tofaktor-kode',
  feltRef,
  kode,
  onKode,
  feil,
  av,
  helt,
  synligEtikett,
  autoFokus,
}: {
  id?: string;
  feltRef?: RefObject<HTMLInputElement | null>;
  kode: string;
  onKode: (v: string) => void;
  feil: string | null;
  av: boolean;
  /** Sifrene i h1-størrelse: feltet er skjermens helt (utfordringen). */
  helt?: boolean;
  /** Etiketten synlig for øyet også, fordi overskriften sier noe annet. */
  synligEtikett?: boolean;
  autoFokus?: boolean;
}) {
  return (
    <div className="panel-felt">
      <label htmlFor={id} className="panel-skjult">
        Kode fra appen
      </label>
      {synligEtikett ? (
        <span aria-hidden="true" className="panel-felt-etikett">
          Kode fra appen
        </span>
      ) : null}
      <input
        id={id}
        ref={feltRef}
        type="text"
        className={helt ? 'panel-kode panel-kode-helt' : 'panel-kode'}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        required
        aria-invalid={feil ? true : undefined}
        value={kode}
        onChange={(e) => onKode(e.target.value)}
        disabled={av}
        autoFocus={autoFokus}
      />
      {feil ? (
        <p role="alert" className="panel-feltfeil">
          <Ikon navn="alert-circle-outline" />
          {feil}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Tekstknappen som bytter visning: «Kan ikke skanne?» på en pekerenhet, «Åpne i
 * autentiseringsappen» på en berøringsenhet (appen er på samme enhet som koden, og da kan
 * den ikke skannes), og «Vis QR-koden» tilbake. Finnes ikke før oppsettet er der å vise.
 */
export function Visningsknapp({ oppsett, visning, onVisning }: { oppsett: TotpOppsett | null; visning: Visning; onVisning: (v: Visning) => void }) {
  const beroering = useBeroering();
  if (!oppsett) return null;
  if (visning === 'noekkel') {
    return (
      <button type="button" className="panel-knapp-lenke" onClick={() => onVisning('qr')}>
        Vis QR-koden
      </button>
    );
  }
  if (beroering) {
    return (
      <button type="button" className="panel-knapp-lenke" onClick={() => window.open(oppsett.uri, '_self')}>
        Åpne i autentiseringsappen
      </button>
    );
  }
  return (
    <button type="button" className="panel-knapp-lenke" onClick={() => onVisning('noekkel')}>
      Kan ikke skanne?
    </button>
  );
}
